/**
 * Sysadmin Tenant Detail — Users tab (R5 §4.8 read-only roster).
 *
 * Spec §4.8 [v1]: Tenant user list. Read-only — sysadmin uses
 * impersonation (Wave 4) for per-user actions; this surface exists
 * specifically so sysadmin doesn't NEED to impersonate just to see
 * who's in a tenant.
 *
 * Cursor-paginated with search by email/firstName/lastName.
 *
 * NO actions. Read-only by design per ADR 12.
 */

import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  ShieldCheck, Search, ChevronLeft, ChevronRight, KeyRound, X,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useTenantUsers, useTenantDetail } from '@/lib/use-tenants';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/users')({
  component: TenantUsersTab,
});

function TenantUsersTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/users' });
  const { data: detail } = useTenantDetail(tenantId);
  // Cursor stack so we can page backwards. Each entry is the cursor
  // used to fetch the page at that index; page 0 has cursor=undefined.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const cursor = cursorStack[pageIdx] ?? undefined;
  const { data, isLoading, error } = useTenantUsers(tenantId, {
    limit: 50,
    cursor: cursor ?? undefined,
    q: activeSearch || undefined,
  });

  function applySearch() {
    setActiveSearch(searchInput.trim());
    setCursorStack([null]);
    setPageIdx(0);
  }
  function clearSearch() {
    setSearchInput('');
    setActiveSearch('');
    setCursorStack([null]);
    setPageIdx(0);
  }
  function pageNext() {
    if (!data?.nextCursor) return;
    const next = [...cursorStack.slice(0, pageIdx + 1), data.nextCursor];
    setCursorStack(next);
    setPageIdx(pageIdx + 1);
  }
  function pagePrev() {
    if (pageIdx === 0) return;
    setPageIdx(pageIdx - 1);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                Users in this tenant
              </CardTitle>
              <CardDescription>
                Read-only roster. Per-user actions and impersonation are
                Wave 4 (ADR 12). {detail?.stats.userCount.toLocaleString() ?? '—'} total.
              </CardDescription>
            </div>
            <div className="shrink-0">
              <div className="flex items-center gap-1.5">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Search email/name…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applySearch();
                    }}
                    className="pl-8 h-8 w-64 text-xs"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={applySearch} disabled={!searchInput}>
                  Search
                </Button>
                {activeSearch && (
                  <Button size="sm" variant="ghost" onClick={clearSearch} title="Clear search">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center animate-pulse">
              Loading users…
            </div>
          ) : error ? (
            <div className="px-6 py-8 text-sm text-destructive text-center">
              Failed to load: {error.message}
            </div>
          ) : !data || data.users.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground text-center">
              {activeSearch
                ? `No users match "${activeSearch}".`
                : 'No users in this tenant yet.'}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>MFA</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.users.map((u) => {
                    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium text-xs">{u.email}</TableCell>
                        <TableCell className="text-xs">
                          {fullName || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.status ? (
                            <Badge
                              variant={u.status === 'active' ? 'default' : 'secondary'}
                              className="text-[10px] capitalize"
                            >
                              {u.status}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.mfaEnabled ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                              <KeyRound className="h-3 w-3" />
                              On
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">Off</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular-nums">
                          {u.lastLoginAt
                            ? new Date(u.lastLoginAt).toLocaleString()
                            : <span>never</span>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular-nums">
                          {new Date(u.joinedAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between px-6 py-3 border-t text-xs text-muted-foreground">
                <div>
                  Page {pageIdx + 1}
                  {activeSearch
                    ? ` — filtered by "${activeSearch}" (${data.users.length} shown)`
                    : ` — showing ${data.users.length} of ${data.total} total`}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={pageIdx === 0}
                    onClick={pagePrev}
                    className="h-7"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!data.nextCursor}
                    onClick={pageNext}
                    className="h-7"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
